// Riquadro in fondo al PDF del preventivo (Riepilogo | Dettagli pagamento | Firma),
// impostato come un conto. Unico per lista, creazione e modifica [sc-48728].
// Guida: riepilogo-pdf-credito-e-ordini-chiusi.md (sc-48721), §A3–§A8.
import { daRestituire } from "./pagamenti";

// Dati aziendali di tutti i PDF del preventivo.
export const INTESTAZIONE_AZIENDA = [
  "BARCIO CERAMICHE SRL",
  "VIALE SCALA GRECA, 406",
  "96100 SIRACUSA",
  "P.IVA: 02142710892",
  "C.UNIVOCO: SU9YNJA",
  "barcioceramichesrl@gmail.com",
  "barcioceramichesrl@pec.it",
];

export const DATI_BANCARI = [
  { label: "IBAN", value: "IT90V0306917100100000063488" },
  { label: "Intestato a", value: "BARCIO CERAMICHE SRL" },
  { label: "Banca", value: "BANCA INTESA SAN PAOLO" },
];

// Il meno è un trattino medio (U+2013): il segno matematico «−» (U+2212)
// non è nei font standard di jsPDF e uscirebbe come un carattere a caso.
const MENO = "–";

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const num = (v) => Number(v) || 0;

/** Importo in formato italiano: 1234.5 → "1.234,50". */
export function eur(n) {
  const [intero, decimali] = Math.abs(round2(num(n)))
    .toFixed(2)
    .split(".");
  const segno = round2(num(n)) < 0 ? "-" : "";
  return `${segno}${intero.replace(/\B(?=(\d{3})+(?!\d))/g, ".")},${decimali}`;
}

/** IVA totale delle righe della tabella articoli ({ lineTotal, iva } IVA compresa). */
export function ivaDaRighe(righe) {
  return round2(
    (righe || []).reduce((tot, { lineTotal, iva }) => {
      const lordo = num(lineTotal);
      return tot + lordo - lordo / (1 + num(iva) / 100);
    }, 0)
  );
}

/**
 * Etichetta di una riga in percentuale [sc-48768].
 * La percentuale si scrive solo se torna con l'importo: se non torna con
 * nessuna delle basi possibili, l'importo è rimasto indietro rispetto alla
 * sua percentuale (vedi sc-48789) e stamparla direbbe il falso.
 * Quando la base non è la riga precedente, si scrive per esteso.
 */
function etichettaConPercentuale(voce, importo, pct, basi) {
  if (!pct) return voce;
  for (const { valore, esplicita } of basi) {
    if (Math.abs(round2((valore * pct) / 100) - importo) <= 0.01) {
      return esplicita
        ? // «€» attaccato al numero: se l'etichetta va a capo non si spezza
          // fra il simbolo e la cifra.
          `${voce} (${pct}% su €${eur(valore)})`
        : `${voce} (${pct}%)`;
    }
  }
  return voce;
}

/**
 * Righe della colonna Riepilogo. Riceve i soli importi grezzi del preventivo.
 * L'imponibile è il totale articoli meno l'IVA: così le due righe grigie
 * sommano sempre esattamente al «Totale articoli» calcolato dal server.
 */
export function righeRiepilogo({
  iva = 0,
  totaleArticoli = 0,
  maggiorazione = 0,
  maggiorazionePct = 0,
  sconto = 0,
  scontoPct = 0,
  acconto = 0,
  // [sc-49299] spese aggiuntive senza IVA: [{ label, amount }]
  spese = [],
  // [sc-49299] un versamento per riga ({ label, amount }), già controllato
  // sulla somma (righeAcconti); null o vuoto = riga unica «Acconto»
  acconti = null,
  // [sc-49299] finestra di stampa: righe che si possono togliere. Gli importi
  // restano nei totali, così il conto torna sempre.
  mostra = {},
} = {}) {
  const vedi = (k) => mostra[k] !== false;
  const totale = round2(num(totaleArticoli));
  // `iva` e' l'IVA delle righe: un numero, oppure divisa per aliquota, e
  // allora nel riepilogo esce una riga per aliquota. Per aliquota si accetta
  // la sola IVA ({ 22: 17.6 }) o anche la sua base
  // ({ 22: { imponibile: 80, iva: 17.6 } }), che finisce nell'etichetta.
  const perAliquota = iva !== null && typeof iva === "object" ? iva : null;
  const ivaDi = (v) =>
    v !== null && typeof v === "object" ? num(v.iva) : num(v);
  const baseDi = (v) =>
    v !== null && typeof v === "object" && v.imponibile != null
      ? num(v.imponibile)
      : null;
  const imposta = perAliquota
    ? round2(Object.values(perAliquota).reduce((t, v) => t + ivaDi(v), 0))
    : round2(num(iva));
  const piu = round2(num(maggiorazione));
  const pct = num(scontoPct);
  const meno =
    num(sconto) > 0 ? round2(num(sconto)) : round2((totale * pct) / 100);
  const versato = round2(num(acconto));
  // la parte con IVA: articoli scontati o maggiorati
  const totaleArticoliNetto = round2(totale + piu - meno);
  const speseValide = (spese || []).filter((s) => num(s.amount) > 0);
  const totaleSpese = round2(
    speseValide.reduce((t, s) => t + num(s.amount), 0)
  );
  const totalePreventivo = round2(totaleArticoliNetto + totaleSpese);

  const righe = [{ label: "Totale articoli", amount: totale }];
  if (piu > 0) {
    // La maggiorazione si calcola sul totale articoli.
    righe.push({
      label: etichettaConPercentuale(
        "Maggiorazione",
        piu,
        num(maggiorazionePct),
        [{ valore: totale, esplicita: false }]
      ),
      amount: piu,
      sign: "+",
    });
  }
  if (meno > 0) {
    // Lo sconto si calcola sul residuo (totale meno acconto): con un acconto
    // versato la base non è la riga sopra, quindi va scritta.
    righe.push({
      label: etichettaConPercentuale("Sconto", meno, pct, [
        { valore: totale, esplicita: false },
        { valore: round2(totale - versato), esplicita: true },
      ]),
      amount: meno,
      sign: MENO,
    });
  }
  // Imponibile e IVA sono il dettaglio, non addendi: è l'IVA di quello che il
  // cliente paga, non quella del listino [sc-48789]. Le righe possono avere
  // aliquote diverse, quindi l'IVA delle righe si scala in proporzione invece
  // di dividere per un'aliquota sola. Le spese non hanno IVA: imponibile e
  // IVA sono dei soli articoli scontati o maggiorati [sc-49299].
  const scala = (v) =>
    totale > 0 ? round2((num(v) * totaleArticoliNetto) / totale) : 0;
  // Una riga per aliquota, dalla più alta; senza divisione, una riga sola.
  const righeIva = perAliquota
    ? Object.keys(perAliquota)
        .map(Number)
        .sort((a, b) => b - a)
        .map((aliquota) => {
          const voce = perAliquota[aliquota];
          const base = baseDi(voce);
          return {
            // «€» attaccato al numero: se l'etichetta va a capo non si spezza
            label:
              base === null
                ? `IVA ${aliquota}%`
                : `IVA ${aliquota}% su €${eur(scala(base))}`,
            amount: scala(ivaDi(voce)),
          };
        })
    : [{ label: "IVA", amount: scala(imposta) }];
  const ivaPreventivo = round2(righeIva.reduce((t, r) => t + r.amount, 0));
  const dettaglioIva = [
    {
      label: "Imponibile",
      amount: round2(totaleArticoliNetto - ivaPreventivo),
      tone: "muted",
      indent: true,
    },
    ...righeIva.map((r) => ({ ...r, tone: "muted", indent: true })),
  ];

  const ivaVisibile = vedi("imponibileIva") ? dettaglioIva : [];
  const speseVisibili = speseValide.length > 0 && vedi("spese");
  const totaleRiga = {
    label: "Totale preventivo",
    amount: totalePreventivo,
    tone: "strong",
    // spese tolte dalla stampa ma comprese nel totale: lo si dice
    ...(speseValide.length && !speseVisibili
      ? { nota: "comprese spese aggiuntive" }
      : {}),
  };
  if (speseValide.length) {
    // [sc-49299] con le spese il dettaglio IVA sta sotto gli articoli che
    // descrive, prima delle spese: sotto il totale sembrerebbe comprenderle
    righe.push(...ivaVisibile);
    if (speseVisibili) {
      speseValide.forEach((s) =>
        righe.push({
          label: s.label,
          amount: round2(num(s.amount)),
          sign: "+",
        })
      );
    }
    righe.push({ rule: true }, totaleRiga);
  } else {
    righe.push({ rule: true }, totaleRiga, ...ivaVisibile);
  }

  if (versato > 0) {
    if (acconti?.length && vedi("acconti")) {
      acconti.forEach((a) =>
        righe.push({
          label: a.label,
          ...(a.nota ? { nota: a.nota } : {}),
          amount: round2(num(a.amount)),
          sign: MENO,
        })
      );
    } else {
      righe.push({ label: "Acconto", amount: versato, sign: MENO });
    }
    righe.push({ rule: true });
    // Versato oltre il totale: è un credito del cliente, non un importo
    // negativo da saldare [sc-48768]. Il rimborso avviene fuori da Barcio.
    const credito = daRestituire(totalePreventivo - versato);
    righe.push(
      credito > 0
        ? { label: "Da restituire al cliente", amount: credito, tone: "strong" }
        : {
            label: "Totale da pagare",
            amount: Math.max(0, round2(totalePreventivo - versato)),
            tone: "strong",
          }
    );
  }
  return righe;
}

// Griglia: Riepilogo 40% | Dettagli pagamento 33% | Firma 27%
// [sc-49299] Dettagli pagamento 38% → 33%: la Firma passa da ~30 a ~39 mm
// utili; l'IBAN (47 mm a 9 pt) entra ancora nei 51 mm della colonna centrale
const QUOTE_COLONNE = [0.4, 0.33];
const COL_PAD = 6; // margine interno di ogni colonna
const HEAD_PAD_TOP = 10;
const LINE_H = 6;
const NOTA_H = 4.5; // riga della nota sotto una voce (es. metodo dell'acconto)
const RULE_H = 3;
const SIG_LINE_Y_OFFSET = 12; // spazio minimo fra titolo e linea della firma
const MIN_BLOCK_H = 40;
const BANCA_LABEL_PT = 8;
const BANCA_VALUE_PT = 9;
const BANCA_LABEL_TO_VALUE = 4.5; // dall'etichetta al suo valore
const BANCA_VALUE_TO_LABEL = 6.5; // dal valore all'etichetta successiva
const mm = (n) => Math.round(n * 10) / 10; // bordi a 0,1 mm, niente residui float

/**
 * Disegna il riquadro a partire da `y` (nuova pagina se non ci sta)
 * e restituisce la `y` subito sotto.
 */
export function disegnaRiquadro(
  doc,
  {
    y,
    pageWidth,
    pageHeight,
    marginX,
    marginY,
    righe,
    // [sc-49299] finestra di stampa: colonne facoltative
    datiBancari = true,
    firma = true,
  }
) {
  const larghezza = pageWidth - 2 * marginX;
  // Con tutte e tre le colonne la griglia di sempre; con una colonna in meno
  // l'altra prende lo spazio rimasto; da solo il riepilogo occupa tutto.
  let quoteColonne = [];
  if (datiBancari && firma) quoteColonne = QUOTE_COLONNE;
  else if (datiBancari || firma) quoteColonne = [QUOTE_COLONNE[0]];
  const bordi = [];
  let bordo = marginX;
  quoteColonne.forEach((q) => {
    bordo = mm(bordo + larghezza * q);
    bordi.push(bordo);
  });
  const fine = pageWidth - marginX;
  const bordo1 = bordi[0] ?? fine;
  const bordo2 = bordi[1] ?? bordo1;
  const col1X = marginX + COL_PAD;
  const col1Right = bordo1 - COL_PAD;
  const col2X = bordo1 + COL_PAD;
  // la firma va nell'ultima colonna, qualunque sia
  const col3X = (datiBancari ? bordo2 : bordo1) + COL_PAD;
  const sigEnd = pageWidth - marginX - COL_PAD;

  const fontDi = (row) => {
    if (row.tone === "strong")
      doc.setFont("helvetica", "bold").setFontSize(10.5);
    else if (row.tone === "muted")
      doc.setFont("helvetica", "normal").setFontSize(9);
    else doc.setFont("helvetica", "normal").setFontSize(10);
  };
  const importoDi = (row) =>
    `${row.sign ? `${row.sign} ` : ""}€ ${eur(row.amount)}`;

  // Misurare prima di disegnare: una voce lunga va a capo, l'importo resta
  // sulla sua prima riga. Il font va impostato prima di misurare.
  const layout = righe.map((row) => {
    if (row.rule) return { row, h: RULE_H };
    fontDi(row);
    const x = col1X + (row.indent ? 3 : 0);
    const spazio = col1Right - x - doc.getTextWidth(importoDi(row)) - 3;
    const testo = doc.splitTextToSize(row.label, spazio);
    // [sc-49299] nota sotto la voce, piccola e grigia come imponibile e IVA
    let nota = [];
    if (row.nota) {
      doc.setFont("helvetica", "normal").setFontSize(9);
      nota = doc.splitTextToSize(row.nota, col1Right - x);
    }
    return {
      row,
      x,
      testo,
      nota,
      h: testo.length * LINE_H + nota.length * NOTA_H,
    };
  });
  const leftBody = layout.reduce((tot, l) => tot + l.h, 0);

  // Sotto l'ultima riga di testo restano 8 mm: si toglie UNA riga, così anche
  // la seconda riga di una voce andata a capo resta dentro il riquadro.
  const leftHeight = HEAD_PAD_TOP + 7 + leftBody - LINE_H + 8;
  const corpoCentro =
    DATI_BANCARI.length * BANCA_LABEL_TO_VALUE +
    (DATI_BANCARI.length - 1) * BANCA_VALUE_TO_LABEL;
  const centerHeight = datiBancari ? HEAD_PAD_TOP + 7 + corpoCentro + 8 : 0;
  const rightHeight = firma ? HEAD_PAD_TOP + 2 + 7 + SIG_LINE_Y_OFFSET + 8 : 0;
  const blockH = Math.max(MIN_BLOCK_H, leftHeight, centerHeight, rightHeight);

  if (pageHeight - y < blockH + marginY) {
    doc.addPage();
    y = marginY;
  }

  doc.setFillColor(240, 240, 240);
  doc.rect(marginX, y, larghezza, blockH, "F");

  // Separatori verticali, staccati 5 mm dai bordi del riquadro
  doc.setDrawColor(200);
  bordi.forEach((x) => doc.line(x, y + 5, x, y + blockH - 5));

  // Titoli sulla stessa riga, ciascuno a sinistra della sua colonna
  const titoloY = y + HEAD_PAD_TOP;
  doc.setTextColor(0);
  doc.setFont("helvetica", "bold").setFontSize(12);
  doc.text("Riepilogo", col1X, titoloY);
  if (datiBancari) doc.text("Dettagli pagamento", col2X, titoloY);
  if (firma) doc.text("Firma", col3X, titoloY);

  // Colonna 1: il conto
  let fyLeft = titoloY + 7;
  layout.forEach(({ row, x, testo, nota, h }) => {
    if (row.rule) {
      // la linea sta a metà dello spazio che le è riservato
      const ly = fyLeft - LINE_H / 2 + RULE_H / 2;
      doc.setDrawColor(170);
      doc.line(col1X, ly, col1Right, ly);
      fyLeft += h;
      return;
    }
    fontDi(row);
    doc.setTextColor(row.tone === "muted" ? 110 : 0);
    doc.text(testo, x, fyLeft);
    doc.text(importoDi(row), col1Right, fyLeft, { align: "right" });
    if (nota.length) {
      doc.setFont("helvetica", "normal").setFontSize(9);
      doc.setTextColor(110);
      const notaY = fyLeft + (testo.length - 1) * LINE_H + NOTA_H;
      doc.text(nota, x, notaY, { lineHeightFactor: 1.15 });
    }
    fyLeft += h;
  });

  // Colonna 2: etichetta grigia sopra, valore nero sotto
  let fb = titoloY + 7;
  (datiBancari ? DATI_BANCARI : []).forEach(({ label, value }, i) => {
    if (i > 0) fb += BANCA_VALUE_TO_LABEL;
    doc.setFont("helvetica", "normal").setFontSize(BANCA_LABEL_PT);
    doc.setTextColor(110);
    doc.text(label, col2X, fb);
    fb += BANCA_LABEL_TO_VALUE;
    doc.setFontSize(BANCA_VALUE_PT);
    doc.setTextColor(0);
    doc.text(value, col2X, fb);
  });

  // Colonna 3: la linea della firma in fondo al riquadro
  const sigLineY = y + blockH - 8;
  doc.setDrawColor(0);
  if (firma) doc.line(col3X, sigLineY, sigEnd, sigLineY);

  // colori di default per quello che segue
  doc.setTextColor(0);
  doc.setDrawColor(0);
  doc.setFont("helvetica", "normal");

  return y + blockH + 5;
}
